import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowUpRight, ArrowDownRight, Robot, Sparkle } from "@phosphor-icons/react";

const KPI = ({ label, value, unit, delta, testid }) => {
  const positive = (delta ?? 0) >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="itsme-card p-5 itsme-card-hover" data-testid={testid}>
      <div className="data-label">{label}</div>
      <div className="mt-3 flex items-end gap-2">
        <div className="font-heading text-3xl font-bold tracking-tight">{value}</div>
        {unit && <div className="text-sm text-slate-400 pb-1">{unit}</div>}
      </div>
      {delta !== undefined && (
        <div className={`mt-2 text-xs flex items-center gap-1 ${positive ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
          {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {Math.abs(delta)}% vs last week
        </div>
      )}
    </motion.div>
  );
};

export default function HomeOverview() {
  const [k, setK] = useState(null);
  const [agents, setAgents] = useState([]);
  const [advisories, setAdvisories] = useState([]);

  useEffect(() => {
    (async () => {
      const [kpis, ag, adv] = await Promise.all([
        api.get("/overview/kpis"), api.get("/overview/agents"), api.get("/advisories")
      ]);
      setK(kpis.data); setAgents(ag.data); setAdvisories(adv.data);
    })();
  }, []);

  if (!k) return <div className="data-label">Loading overview…</div>;

  return (
    <div className="space-y-6" data-testid="home-overview">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="data-label">Command Center</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Home Overview
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">
            Health snapshot, active advisories, and North-Star KPIs across every discovered process.
          </p>
        </div>
        <div className="itsme-card px-4 py-2 flex items-center gap-2 text-xs text-slate-300">
          <span className="status-dot active" /> All agents online
        </div>
      </div>

      {/* AI Insight Ribbon */}
      <div className="ai-ribbon rounded-md p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="ai-ribbon">
        <div className="flex items-center gap-2">
          <Sparkle size={16} className="text-[#00E5FF]" />
          <span className="data-label" style={{ color: "#00E5FF" }}>What changed / Next action</span>
        </div>
        <div className="text-sm text-slate-200 flex-1">
          {k.anomalies_open} open anomalies detected. Recommended: automate L1→L2 triage in
          <span className="text-white font-medium"> Incident Resolution </span>
          — projected 40% cycle-time reduction.
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-processes" label="Discovered Processes" value={k.processes} delta={6} />
        <KPI testid="kpi-health" label="Avg Process Health" value={k.avg_process_health} unit="/100" delta={4} />
        <KPI testid="kpi-anomalies" label="Open Anomalies" value={k.anomalies_open} delta={-12} />
        <KPI testid="kpi-sla" label="SLA Compliance" value={k.sla_compliance} unit="%" delta={2} />
      </div>

      {/* Trend + Agents */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="itsme-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="data-label">14-day trend</div>
              <div className="font-heading text-lg font-semibold mt-1">Throughput vs Anomalies</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={k.trend}>
                <defs>
                  <linearGradient id="thr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#272A35" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={10}
                       tickFormatter={(d) => new Date(d).toLocaleDateString("en", { month: "short", day: "numeric" })} />
                <YAxis stroke="#6b7280" fontSize={10} />
                <Tooltip contentStyle={{ background: "#13151A", border: "1px solid #272A35", borderRadius: 6 }} />
                <Area type="monotone" dataKey="throughput" stroke="#00E5FF" fill="url(#thr)" strokeWidth={2} />
                <Line type="monotone" dataKey="anomalies" stroke="#FFB000" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="itsme-card p-5">
          <div className="data-label">AI Agent Fleet</div>
          <div className="font-heading text-lg font-semibold mt-1 mb-4">6 Specialized Agents</div>
          <div className="space-y-3">
            {agents.map((a) => (
              <div key={a.key} data-testid={`agent-${a.key}`}
                   className="flex items-center gap-3 p-2 rounded hover:bg-[#1C1F26] transition-colors">
                <Robot size={18} className="text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{a.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">{a.model}</div>
                </div>
                <span className={`status-dot ${a.status === "active" ? "active" : "idle"}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Advisories */}
      <div className="itsme-card p-5">
        <div className="data-label">Active Advisories</div>
        <div className="font-heading text-lg font-semibold mt-1">Next Best Actions</div>
        <div className="mt-4 space-y-3">
          {advisories.length === 0 && <div className="text-sm text-slate-500">No advisories.</div>}
          {advisories.map((adv) => (
            <div key={adv.id} data-testid={`advisory-${adv.id}`}
                 className={`itsme-card itsme-card-hover p-4 sev-${adv.severity === "warning" ? "high" : "medium"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{adv.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{adv.next_best_action}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{adv.type}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
